import { useState, useEffect } from 'react';
import axios from 'axios';
import { getCernerFpa, CERNER_PID_TYPE } from '../utils/cerner';
import { getResolvedSearchLocation } from '../utils/location';
import { getPersistedTokenValue } from '../tracking/persisted-tokens';
import { fetchSessionInfo } from '../direct-booking/api/smart';

/**
 * Unified hook that handles both CCF and SMART on FHIR demographics location
 * based on the customer configuration. This hook ensures only the appropriate
 * integration makes API calls, avoiding unnecessary requests.
 *
 * @param {Object} params - The hook parameters
 * @param {Object} params.config - customer Config from redux
 * @param {string} params.customerCode - customer code string (only needed for CCF)
 * @returns {Object} {
 *   patientZipSearchLocation: geo-resolved location (lat/lng or zip)
 *   patientZip: patient zipcode if one was fetched
 *   patientZipPending: if demographics are in the loading state
 *   patientZipError: an error, if any, raised when getting demographics
 * }
 */
export default function useDemographicsLocation({ config, customerCode }) {
  const [fetchedZip, setFetchedZip] = useState(undefined);
  const [searchLocation, setSearchLocation] = useState(undefined);
  const [error, setError] = useState(undefined);

  // Determine which integration is enabled
  const isSmartOnFhirEnabled = Boolean(config?.third_party?.cerner?.iss);
  const isCcfEnabled = Boolean(
    config?.third_party?.cerner_healthelife?.search_auto_radius
  );

  // Only set pending if one of the integrations is enabled
  const [isPending, setIsPending] = useState(
    isSmartOnFhirEnabled || isCcfEnabled
  );

  useEffect(() => {
    // Early return if no integration is enabled
    if (!isSmartOnFhirEnabled && !isCcfEnabled) {
      setIsPending(false);
      return;
    }

    async function fetchDemographics() {
      try {
        setIsPending(true);

        let demographicsResponse;

        if (isSmartOnFhirEnabled) {
          // SMART on FHIR integration
          const smartSessionInfo = await fetchSessionInfo();

          if (!smartSessionInfo?.patientId) {
            setIsPending(false);
            return;
          }

          demographicsResponse = await fetchSmartPatientDemographics({
            patientId: smartSessionInfo.patientId,
            accessToken: smartSessionInfo.accessToken,
            serverUrl: smartSessionInfo.serverUrl
          });
        } else if (isCcfEnabled) {
          // Legacy CCF integration using BCS token
          const bcsToken = getPersistedTokenValue('bcsToken');
          const cernerFpa = getCernerFpa(bcsToken);

          if (!cernerFpa) {
            setIsPending(false);
            return;
          }

          demographicsResponse = await fetchEhrPatientDemographics({
            customerCode,
            pid: cernerFpa,
            pid_type: CERNER_PID_TYPE
          });
        }

        if (!demographicsResponse?.result?.address?.zip) {
          setIsPending(false);
          return;
        }

        let zip = demographicsResponse.result.address.zip;

        if (typeof zip === 'string' && zip.length > 5) {
          zip = zip.substring(0, 5);
        }

        let searchLocation = zip;

        try {
          searchLocation = await getResolvedSearchLocation(zip);
        } catch (e) {
          // ignore, we'll just use `zip` as the location
        }

        setSearchLocation(searchLocation);
        setFetchedZip(zip);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsPending(false);
      }
    }

    fetchDemographics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmartOnFhirEnabled, isCcfEnabled]);

  return {
    patientZipSearchLocation: searchLocation,
    patientZip: fetchedZip,
    patientZipPending: isPending,
    patientZipError: error
  };
}

// Legacy CCF integration - fetch demographics using patient lookup API
async function fetchEhrPatientDemographics({ customerCode, pid, pid_type }) {
  const response = await axios.post(
    `/api/v2/appointments/${encodeURIComponent(customerCode)}/patients/lookup`,
    {
      patient_id: { pid, pid_type }
    }
  );

  return response.data;
}

// SMART on FHIR integration - make FHIR call directly from client
async function fetchSmartPatientDemographics({
  patientId,
  accessToken,
  serverUrl
}) {
  // Make FHIR API call directly from the client
  const response = await axios.get(`${serverUrl}/Patient/${patientId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/fhir+json'
    }
  });

  const patient = response.data;

  // Extract address from FHIR Patient resource
  const addresses = patient.address || [];
  const homeAddress =
    addresses.find((addr) => addr.use === 'home') || addresses[0];

  if (!homeAddress) {
    throw new Error('No address found in patient demographics');
  }

  // Transform to match the expected response format
  return {
    result: {
      address: {
        zip: homeAddress.postalCode
      }
    }
  };
}
